import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  Box,
  styled,
  alpha,
  CircularProgress,
} from '@mui/material'
import { FiCheckCircle, FiInbox } from 'react-icons/fi'

// ---- Styled ----

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}))

const SuccessBox = styled(Box)(({ theme }) => ({
  padding: 20,
  textAlign: 'center',
  color: '#059669',
  fontWeight: 600,
}))

// ---- Component ----

const FeedbackModal = ({ isOpen, onClose, onSubmit }) => {
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Il messaggio non può essere vuoto.')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      await onSubmit(message)
      setSuccess(true)
      setMessage('')

      setTimeout(() => {
        setSuccess(false)
        onClose()
      }, 2000)
    } catch (e) {
      const raw = e.response?.data?.detail
      if (Array.isArray(raw)) {
        setError(raw.map(r => r.msg).join(', '))
      } else if (typeof raw === 'object') {
        setError(JSON.stringify(raw))
      } else {
        setError(raw || 'Impossibile inviare il feedback.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={isSubmitting ? () => {} : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
          bgcolor: theme =>
            theme.palette.mode === 'dark'
              ? alpha('#fff', 0.06)
              : theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <IconWrapper>
          <FiInbox size={48} />
        </IconWrapper>

        <Typography variant="h5" fontWeight={600}>
          Invia un Feedback
        </Typography>

        <Typography variant="body2" color="text.secondary" mt={1}>
          Il tuo parere è importante. Aiutaci a migliorare F.R.A.N.C.A.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <SuccessBox>
            <FiCheckCircle size={32} />
            <Typography mt={1}>Grazie! Il tuo feedback è stato inviato.</Typography>
          </SuccessBox>
        ) : (
          <TextField
            fullWidth
            multiline
            placeholder="Scrivi qui il tuo messaggio..."
            value={message}
            disabled={isSubmitting}
            onChange={e => setMessage(e.target.value)}
            error={Boolean(error)}
            helperText={error}
            minRows={4}
          />
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ justifyContent: 'center', gap: 1, p: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Annulla
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? <CircularProgress size={16} thickness={5} /> : null
            }
          >
            {isSubmitting ? 'Invio...' : 'Invia'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default FeedbackModal
